import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isPlatformOperator } from '../beta/beta-service'
import {
  getContractorVerificationWorkspace,
  reviewContractorProfile,
  reviewComplianceRecord,
  type ComplianceQueueItem,
  type ContractorVerificationQueueItem,
} from './marketplace-admin-service'

function formatDate(value: string | null) {
  return value ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : 'Not recorded'
}

function ReviewCard({ item }: { item: ContractorVerificationQueueItem }) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState(item.rejectionReason ?? '')
  const mutation = useMutation({
    mutationFn: (decision: 'verified' | 'rejected') =>
      reviewContractorProfile({
        userId: item.userId,
        decision,
        reason,
        insuranceVerified: false,
        backgroundCheckVerified: false,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contractor-verification'] }),
  })

  return (
    <article className="provider-review-card">
      <div className="provider-review-heading">
        <div>
          <span className={`status-badge status-${item.verificationStatus}`}>{item.verificationStatus}</span>
          <h2>{item.businessName}</h2>
          <p>{item.email}</p>
        </div>
        <div className="provider-review-dates">
          <span>Submitted</span>
          <strong>{formatDate(item.submittedAt)}</strong>
        </div>
      </div>

      <dl className="provider-review-details">
        <div><dt>Trade summary</dt><dd>{item.tradeSummary || 'Not provided'}</dd></div>
        <div><dt>Service area</dt><dd>{item.city}, {item.region} {item.postalCode} · {item.serviceRadiusMiles} miles</dd></div>
        <div><dt>Services</dt><dd>{item.categories.map((category) => category.name).join(', ') || 'None selected'}</dd></div>
        <div><dt>Phone</dt><dd>{item.phone || 'Not provided'}</dd></div>
      </dl>

      <p className="legal-warning">Profile approval does not verify identity, insurance, licensing, tax, workers compensation, or background screening. Review those records separately below.</p>

      <label className="field">
        <span>Review notes / rejection reason</span>
        <textarea value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Required when rejecting a provider." />
      </label>

      {mutation.error ? <p className="form-error">{mutation.error.message}</p> : null}
      <div className="provider-review-actions">
        <button
          type="button"
          className="secondary-button danger-button"
          disabled={mutation.isPending || !reason.trim()}
          onClick={() => mutation.mutate('rejected')}
        >
          Reject provider
        </button>
        <button type="button" className="primary-button" disabled={mutation.isPending} onClick={() => mutation.mutate('verified')}>
          Approve provider
        </button>
      </div>
    </article>
  )
}

function ComplianceReviewCard({ item }:{ item:ComplianceQueueItem }) {
  const qc=useQueryClient(); const [reason,setReason]=useState('')
  const mutation=useMutation({ mutationFn:(decision:'verified'|'rejected'|'review_required')=>reviewComplianceRecord(item,decision,reason),onSuccess:()=>qc.invalidateQueries({ queryKey:['contractor-verification'] }) })
  return <article className="provider-review-card"><div className="provider-review-heading"><div><span className="status-badge">{item.status}</span><h3>{item.credential_type.replaceAll('_',' ')}</h3><p>Contractor {item.contractor_user_id}</p></div></div>
    <label className="field"><span>Required structured decision reason</span><textarea value={reason} onChange={event=>setReason(event.target.value)} /></label>
    {mutation.error && <p className="form-error">{mutation.error.message}</p>}<div className="provider-review-actions"><button disabled={!reason.trim()||mutation.isPending} onClick={()=>mutation.mutate('rejected')}>Reject</button><button disabled={!reason.trim()||mutation.isPending} onClick={()=>mutation.mutate('review_required')}>Request correction</button><button disabled={!reason.trim()||mutation.isPending} onClick={()=>mutation.mutate('verified')}>Verify</button></div></article>
}

export function MarketplaceAdministrationPage() {
  const operator = useQuery({ queryKey: ['platform-operator'], queryFn: isPlatformOperator })
  const workspace = useQuery({
    queryKey: ['contractor-verification'],
    queryFn: getContractorVerificationWorkspace,
    enabled: operator.data === true,
  })

  if (operator.isLoading) return <main className="page"><p>Checking platform access…</p></main>
  if (!operator.data) {
    return <main className="page"><div className="error-panel"><h1>Access denied</h1><p>Only PropertyReady platform operators can review providers.</p></div></main>
  }
  if (workspace.isLoading) return <main className="page"><p>Loading provider verification queue…</p></main>
  if (workspace.error) return <main className="page"><div className="error-panel"><h1>Review queue unavailable</h1><p>{workspace.error.message}</p></div></main>

  const pending = (workspace.data?.queue ?? []).filter((item) => item.verificationStatus === 'pending')

  return (
    <main className="page provider-admin-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Marketplace trust & safety</p>
          <h1>Provider approvals</h1>
          <p>Review contractor applications, document verification decisions and control marketplace access.</p>
        </div>
        <button type="button" className="primary-button" onClick={() => { void workspace.refetch() }}>Refresh queue</button>
      </header>

      <section className="provider-admin-summary">
        <div><span>Awaiting review</span><strong>{pending.length}</strong></div>
        <div><span>Total profiles</span><strong>{workspace.data?.queue.length ?? 0}</strong></div>
        <div><span>Recorded decisions</span><strong>{workspace.data?.history.length ?? 0}</strong></div>
      </section>

      <section>
        <h2>AMIGO Contractor Intelligence™</h2>
        <p className="legal-warning">LEGAL AND FAIRNESS REVIEW REQUIRED BEFORE PUBLIC LAUNCH. Public scores, recommendations and ranking remain disabled by default.</p>
        <div className="provider-admin-summary"><div><span>Metric disputes</span><strong>{workspace.data?.intelligence.disputes.length??0}</strong></div><div><span>Review moderation</span><strong>{workspace.data?.intelligence.reviewModeration.length??0}</strong></div><div><span>Claims awaiting determination</span><strong>{workspace.data?.intelligence.claims.length??0}</strong></div><div><span>Unusual score changes</span><strong>{workspace.data?.intelligence.unusualChanges.length??0}</strong></div><div><span>Insufficient evidence</span><strong>{workspace.data?.intelligence.insufficientEvidence??0}</strong></div><div><span>Potential manipulation review</span><strong>{workspace.data?.intelligence.manipulationFlags.length??0}</strong></div><div><span>Stale snapshots</span><strong>{workspace.data?.intelligence.staleSnapshots??0}</strong></div><div><span>Scoring versions</span><strong>{workspace.data?.intelligence.versions.length??0}</strong></div></div>
        <details><summary>Recommendation audits</summary><p>{workspace.data?.intelligence.recommendationAudits.length??0} auditable explanations recorded.</p></details>
      </section>

      <section>
        <h2>Compliance review queue</h2>
        <p>Only verify a record after direct evidence review or a configured provider result. All decisions are audited.</p>
        {workspace.data?.compliance.pending.length ? <div className="provider-review-list">{workspace.data.compliance.pending.map((item,index)=><ComplianceReviewCard key={`${item.credential_type}-${item.credential_id ?? item.contractor_user_id}-${index}`} item={item} />)}</div>:<div className="empty-state"><h3>Queue clear</h3><p>No compliance records await review.</p></div>}
      </section>

      <section>
        <h2>Expiring credentials</h2><p>{workspace.data?.compliance.expiring.length ?? 0} credentials expire within 30 days.</p>
        <h2>Background-review cases</h2><p>{workspace.data?.compliance.backgroundReview.length ?? 0} cases require restricted review.</p>
      </section>

      <section>
        <h2>Awaiting review</h2>
        {pending.length ? <div className="provider-review-list">{pending.map((item) => <ReviewCard key={item.userId} item={item} />)}</div> : <div className="empty-state"><h3>Queue clear</h3><p>No provider applications are awaiting review.</p></div>}
      </section>

      <section className="provider-review-history">
        <h2>Recent decisions</h2>
        {workspace.data?.history.length ? (
          <div className="table-scroll">
            <table>
              <thead><tr><th>Provider</th><th>Decision</th><th>Reviewed by</th><th>Date</th><th>Reason</th></tr></thead>
              <tbody>
                {workspace.data.history.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.businessName}</strong><br /><span>{item.email}</span></td>
                    <td><span className={`status-badge status-${item.decision}`}>{item.decision}</span></td>
                    <td>{item.reviewedBy}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>{item.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No decisions recorded yet.</p>}
      </section>
    </main>
  )
}
